import moment from "moment";
import "moment/locale/fr";
import { DocumentInfo } from "../../Repositories/DocumentRepository";
import { statusOf } from "../../Components/App/Documents/documentStatus";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import { euros } from "../DeclarationV2/format";

/**
 * Lecture d'un document de l'API (`DocumentInfo`) pour « Mes documents »
 * repensé (maquette 9a) : catégorie, repère visuel, titre et sous-ligne,
 * statut en pastille. Les règles de statut restent celles de `documentStatus`.
 */

/** Catégories d'affichage, dans l'ordre des onglets (les récapitulatifs comptent comme des factures). */
export const DOCUMENT_CATEGORIES: Array<{ key: string; label: string }> = [
    { key: "quote", label: "Devis" },
    { key: "invoice", label: "Factures" },
    { key: "attestation", label: "Attestations" },
    { key: "tax_credit", label: "Attestations fiscales" },
    { key: "transport", label: "Abonnements" },
];

export const DOCUMENTS_TAB_PARAM = "onglet";

export const isDocumentCategory = (value: string | null | undefined): boolean =>
    !!value && DOCUMENT_CATEGORIES.some((c) => c.key === value);

export const categoryOf = (doc: DocumentInfo): string => {
    if (doc.category) return doc.category;
    if (doc.type === "invoice" || doc.type === "invoice_recap") return "invoice";
    if (doc.type === "travel_attestation" || doc.type === "deposit_attestation") return "attestation";
    return doc.type;
};

/** Repère visuel à gauche de la ligne. */
export const documentBadge = (doc: DocumentInfo): string => {
    switch (categoryOf(doc)) {
        case "quote": return "DEV";
        case "invoice": return "FAC";
        case "attestation": return "ATT";
        case "tax_credit": return "FIS";
        case "transport": return "ABO";
        default: return "DOC";
    }
};

/** Normalise pour la recherche : minuscules et sans accents. */
export const normalize = (value: string): string => value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const sameName = (a: string, b: string): boolean => normalize(a).trim() === normalize(b).trim();

/** Élève du document quand il apporte une information (différent du titulaire). */
export const childNameOf = (doc: DocumentInfo): string | null =>
    doc.childName && !(doc.clientName && sameName(doc.childName, doc.clientName)) ? doc.childName : null;

/** Nom mis en avant : l'élève, à défaut le titulaire du compte. */
export const documentPersonName = (doc: DocumentInfo): string | null => childNameOf(doc) ?? doc.clientName ?? null;

/** Sous-ligne « parent, Jita Ali » quand l'élève est mis en avant. */
export const documentParentLine = (doc: DocumentInfo): string | null =>
    childNameOf(doc) && doc.clientName ? `parent, ${doc.clientName}` : null;

const typeWord = (doc: DocumentInfo): string => {
    switch (doc.type) {
        case "quote": return "Devis";
        case "invoice": return "Facture";
        case "invoice_recap": return "Récapitulatif de facturation";
        case "travel_attestation": return "Attestation de déplacement";
        case "deposit_attestation": return "Attestation d'acompte";
        case "tax_credit": return "Attestation fiscale";
        case "transport": return "Abonnement";
        default: return "Document";
    }
};

/** « Devis pour Daphi Dock », « Abonnement Navigo pour Daphi Dock », « Attestation fiscale 2026 ». */
export const documentTitle = (doc: DocumentInfo): string => {
    const name = documentPersonName(doc);
    if (doc.type === "transport") {
        const base = doc.label || "Abonnement de transport";
        return name ? `${base} pour ${name}` : base;
    }
    if (doc.type === "tax_credit") return doc.label || typeWord(doc);
    return name ? `${typeWord(doc)} pour ${name}` : (doc.label || typeWord(doc));
};

const periodLabel = (from: string, until: string): string => {
    const a = moment(from, "YYYY-MM-DD");
    const b = moment(until, "YYYY-MM-DD");
    const day = (d: moment.Moment) => (d.date() === 1 ? "1er" : String(d.date()));
    if (a.isSame(b, "month")) return `du ${day(a)} au ${day(b)} ${b.format("MMMM YYYY")}`;
    return `du ${day(a)} ${a.format("MMMM")} au ${day(b)} ${b.format("MMMM YYYY")}`;
};

/** « Q-20261101-0009, 300,00 € TTC », « Preuve de déplacement, du 1er au 30 novembre 2026 ». */
export const documentSubline = (doc: DocumentInfo): string => {
    if (doc.type === "transport") {
        return doc.validFrom && doc.validUntil ? `Preuve de déplacement, ${periodLabel(doc.validFrom, doc.validUntil)}` : "Preuve de déplacement";
    }
    const parts: string[] = [];
    if (doc.number) parts.push(doc.number);
    if (doc.amount != null) parts.push(`${euros(doc.amount)}${doc.type === "quote" ? " TTC" : ""}`);
    if (doc.statusReason && doc.status && doc.status !== "active" && doc.status !== "valid") parts.push(doc.statusReason);
    return parts.join(", ");
};

export interface DocumentPill {
    label: string;
    tone: "primary" | "success" | "warning" | "danger" | "muted";
    tooltip?: string;
}

/** Statut en pastille : mêmes règles que la liste actuelle, les factures sont « émises ». */
export const documentPill = (doc: DocumentInfo): DocumentPill | null => {
    const status = statusOf(doc);
    if (status) {
        const tone: DocumentPill["tone"] = status.color === "success" ? "success"
            : status.color === "warning" ? "warning"
                : status.color === "error" ? "danger"
                    : status.color === "info" ? "primary"
                        : "muted";
        return { label: status.label, tone, tooltip: status.tooltip };
    }
    if (doc.type === "invoice" || doc.type === "invoice_recap") return { label: "Émise", tone: "muted" };
    if (doc.type === "tax_credit") return { label: "Délivrée", tone: "muted" };
    return null;
};

/** Date de tri et de regroupement : date du document, ou début de validité d'un abonnement. */
export const documentDay = (doc: DocumentInfo): moment.Moment | null => {
    const raw = doc.type === "transport" ? (doc.validFrom ?? doc.date) : doc.date;
    if (!raw) return null;
    const m = moment(raw);
    return m.isValid() ? m : null;
};

/** « 1er novembre », « 1er au 30 novembre » pour un abonnement. */
export const documentDateLabel = (doc: DocumentInfo): string => {
    const day = (d: moment.Moment) => (d.date() === 1 ? "1er" : String(d.date()));
    if (doc.type === "transport" && doc.validFrom && doc.validUntil) {
        const a = moment(doc.validFrom, "YYYY-MM-DD");
        const b = moment(doc.validUntil, "YYYY-MM-DD");
        return a.isSame(b, "month") ? `${day(a)} au ${day(b)} ${b.format("MMMM")}` : `${day(a)} ${a.format("MMM")} au ${day(b)} ${b.format("MMM")}`;
    }
    const d = documentDay(doc);
    if (!d) return "…";
    return `${day(d)} ${d.format("MMMM")}${d.isSame(moment(), "year") ? "" : ` ${d.format("YYYY")}`}`;
};

/** En-tête de groupe : « Dimanche 1er novembre 2026 ». */
export const dayHeading = (d: moment.Moment): string => {
    const day = d.date() === 1 ? "1er" : String(d.date());
    return `${capitalizeFirstLetter(d.format("dddd"))} ${day} ${d.format("MMMM YYYY")}`;
};

/** Période rapide du filtre. */
export type DocumentPeriod = "all" | "month" | "quarter" | "year" | "custom";

export const periodBoundsOf = (period: DocumentPeriod): { from: moment.Moment | null; to: moment.Moment | null } => {
    const now = moment();
    switch (period) {
        case "month": return { from: now.clone().startOf("month"), to: now.clone().endOf("month") };
        case "quarter": return { from: now.clone().subtract(2, "month").startOf("month"), to: now.clone().endOf("month") };
        case "year": return { from: now.clone().startOf("year"), to: now.clone().endOf("year") };
        default: return { from: null, to: null };
    }
};

export const prestationWord = (): string => getActivePrestationBrandConfig();
